import axios from "axios";
import { getTurnstileToken, turnstileEnabled } from "./services/turnstile";

const API_URL = process.env.VUE_APP_BASE_URL;

/**
 * Endpoints that require a Turnstile token.
 *
 * security/0.2c (Option 4c): /api/hubspot-request is publicly reachable so the
 * marketing iframe can track anonymous resource views. The token is attached
 * here rather than at the ~17 individual call sites so no call can be missed,
 * and so future HubSpot calls are covered automatically.
 */
const TURNSTILE_PROTECTED = ["hubspot-request"];

function needsTurnstile(config) {
  const url = String(config?.url || "");
  return TURNSTILE_PROTECTED.some((path) => url.includes(path));
}

const instance = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor to set token before sending requests
instance.interceptors.request.use(
  async (config) => {
    const user = JSON.parse(localStorage.getItem("user"));

    if (user && user.token) {
      if (user.expires_at) {
        const expiry = new Date(user.expires_at).getTime();

        // If expired, clear storage & redirect
        if (Date.now() > expiry && !window.tokenExpiredRedirecting) {
          window.tokenExpiredRedirecting = true;
          localStorage.removeItem("user");
          window.location.href = "/login";

          return Promise.reject(new Error("Token expired"));
        }
      }

      config.headers.Authorization = `Bearer ${user.token}`;
    }

    // Attach a Turnstile token to the public HubSpot proxy calls. No-op unless
    // VUE_APP_TURNSTILE_SITEKEY is set at build time. If a token cannot be
    // obtained the request still goes out unchanged — the API decides whether to
    // reject it, and tracking is best-effort rather than something that should
    // block or break the page.
    if (turnstileEnabled() && needsTurnstile(config)) {
      const turnstileToken = await getTurnstileToken();
      if (turnstileToken && config.data && typeof config.data === "object") {
        config.data = { ...config.data, turnstile_token: turnstileToken };
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor: Handle 401 globally
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const lastVisitedURL = localStorage.getItem("lastVisitedURL") ?? "";
      localStorage.clear();
      localStorage.setItem("lastVisitedURL", lastVisitedURL);
      // Check if the current URL has ?v=1
      const urlParams = new URLSearchParams(window.location.search);
      const vParam = urlParams.get("v");

      if (vParam === "1") {
        // If v=1 exists, redirect with verify=0
        location.href = "/login?v=1";
      } else {
        // Normal login redirect
        location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export default instance;
